import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { toggleFavoriteThunk, setOptimisticFavorites } from '../ducks/favorites.duck';
import { createResourceLocatorString } from '../util/routes';
import { hasFavorite, addFavorite, removeFavorite } from '../util/favorites';

const useFavorites = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const routes = useRouteConfiguration();
  const currentUser = useSelector(state => state.user.currentUser);
  const optimisticFavorites = useSelector(state => state.favorites.optimisticFavorites);

  const realFavorites = currentUser?.attributes?.profile?.privateData?.favorites || [];
  const favorites = optimisticFavorites ?? realFavorites;

  const isFavorited = listingId => hasFavorite(favorites, listingId);

  /**
   * Toggle a favorite. `onToggle(wasAdded)` fires at once, for the optimistic
   * UI. Ticket 88 Part 2 (ledger #1382, D-D3): the returned promise settles
   * with the SERVER's answer — `true` when the toggle was saved, `false` when
   * the thunk was rejected (and rolled back), `null` when nothing was toggled
   * (anonymous → sent to login). Anything that RECORDS the toggle (the
   * listing-page favorite actions) waits for `true`; the UI callers ignore it.
   *
   * @param {string} listingId
   * @param {Function} [onToggle]
   * @returns {Promise<boolean|null>} never rejects
   */
  const toggleFavorite = (listingId, onToggle) => {
    if (!currentUser) {
      const from =
        typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}`
          : '/';
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}), { from });
      return Promise.resolve(null);
    }
    const wasAdded = !hasFavorite(favorites, listingId);
    const newFavs = wasAdded
      ? addFavorite(favorites, listingId)
      : removeFavorite(favorites, listingId);
    dispatch(setOptimisticFavorites(newFavs));
    const settled = dispatch(toggleFavoriteThunk(listingId)).then(action =>
      toggleFavoriteThunk.fulfilled.match(action)
    );
    if (onToggle) onToggle(wasAdded);
    return settled;
  };

  return { isFavorited, toggleFavorite };
};

export default useFavorites;
